import { api } from '../api';

/**
 * A fila de quem assina — as chamadas, com o caminho escrito.
 *
 * Mesma razão dos outros arquivos de `api/`: o caminho é o contrato
 * entre a tela e a API, e o teste `26` procura o literal aqui.
 */

export interface OsParaAssinar {
  os_id: string;
  os_numero: string;
  cliente: string | null;
  obra_nome: string | null;
  prazo_contratual: string | null;
  dias_para_prazo: number | null;
  ensaios: number;
  a_verificar: number;
  a_aprovar: number;
  travados: number;
  amostras: number;
  parada_ha: number | null;
  ainda_no_laboratorio: number;
  ja_aprovados: number;
}

export interface EnsaioParaAssinar {
  ensaio_id: string;
  os_id: string;
  os_numero: string;
  amostra_id: string;
  amostra_codigo: string;
  identificacao_recebida: string | null;
  ponto_investigacao: string | null;
  profundidade_inicial: string | null;
  profundidade_final: string | null;
  camada: string | null;
  tipo_amostra: string | null;
  metodo_id: string;
  metodo_codigo: string;
  metodo_nome: string;
  escopo: string | null;
  etapa: 'aguardando_verificacao' | 'aguardando_aprovacao';
  executado_por: string | null;
  executado_por_nome: string | null;
  data_execucao: string | null;
  verificado_por: string | null;
  verificado_por_nome: string | null;
  data_verificacao: string | null;
  dias_na_etapa: number | null;
  motivo_devolucao: string | null;
  versao_congelada: boolean;
  pendencias_que_travam: number;
  /* 0151 — «lançado há X dias»: conta desde o LANÇAMENTO do dado, e não
     desde a última mudança de estado. Não zera na devolução, e é por
     isso que mostra o ensaio que já deu duas voltas. */
  lancado_em: string | null;
  dias_desde_lancamento: number | null;
}

/**
 * Uma linha do ARQUIVO — o ensaio já verificado e aprovado.
 *
 * «Os que já foram aprovados vão para baixo e tem um filtro para
 *  localizar a verificação e aprovação, com a data que ocorreu cada
 *  atividade» — Testesolo, 02/09/2026.
 */
export interface EnsaioAssinado {
  ensaio_id: string;
  os_id: string;
  os_numero: string;
  cliente: string | null;
  obra_nome: string | null;
  amostra_id: string;
  amostra_codigo: string;
  identificacao_recebida: string | null;
  ponto_investigacao: string | null;
  profundidade_inicial: string | null;
  profundidade_final: string | null;
  camada: string | null;
  metodo_id: string;
  metodo_codigo: string;
  metodo_nome: string;
  escopo: string | null;
  executado_por_nome: string | null;
  data_execucao: string | null;
  processado_por_nome: string | null;
  data_processamento: string | null;
  verificado_por: string | null;
  verificado_por_nome: string | null;
  data_verificacao: string | null;
  aprovado_por: string | null;
  aprovado_por_nome: string | null;
  data_aprovacao: string | null;
  lancado_em: string | null;
  dias_ate_verificar: number | null;
  dias_ate_aprovar: number | null;
  dias_do_lancamento_a_aprovacao: number | null;
  mesmo_nome_conferiu_e_aprovou: boolean;
}

export interface PessoaQueAssinou {
  id: string;
  nome: string;
  verificou: boolean;
  aprovou: boolean;
  ensaios: number;
}

export const filaDeAssinatura = (f: { cliente?: string | null } = {}) =>
  api<OsParaAssinar[]>(`/assinatura${f.cliente ? `?cliente=${f.cliente}` : ''}`);

/**
 * O arquivo — o que já foi assinado.
 *
 * A ordenação é do SERVIDOR e não da tela: a consulta corta em 500
 * linhas, e ordenar só a página visível mentiria sobre o que ficou de
 * fora. Na fila, que vem inteira, a ordenação é local.
 */
export const historicoDeAssinatura = (f: {
  busca?: string; os?: string; pessoa?: string; metodo?: string;
  de?: string; ate?: string; ordem?: string; sentido?: 'asc' | 'desc';
  cliente?: string | null;
} = {}) => {
  const q = new URLSearchParams();
  Object.entries(f).forEach(([k, v]) => { if (v) q.set(k, String(v)); });
  const s = q.toString();
  return api<EnsaioAssinado[]>(`/assinatura/historico${s ? `?${s}` : ''}`);
};

/** Quem já assinou alguma coisa — para o seletor do filtro. */
export const pessoasQueAssinaram = () =>
  api<PessoaQueAssinou[]>('/assinatura/historico/pessoas');

/**
 * Uma OS da fila.
 *
 * `tudo_assinado` distingue «acabou» de «não existe» — a view de
 * assinatura só enxerga OS COM pendência, e sem esta marca a tela
 * pintava o trabalho concluído como erro (Testesolo, 02/09/2026).
 */
export const osParaAssinar = (numero: string) =>
  api<{ os: OsParaAssinar; ensaios: EnsaioParaAssinar[];
        tudo_assinado: boolean }>(
    `/assinatura/os/${encodeURIComponent(numero)}`);

/**
 * Verificar um lote.
 *
 * Cada id passa pelo mesmo caminho que passava um a um. O lote é
 * conveniência de tela — e é tudo-ou-nada: assinar seis e falhar no
 * sétimo deixaria quem clicou sem saber o que ficou feito.
 */
export const verificarLote = (corpo: {
  ensaioIds: string[]; observacao?: string;
}) => api<{ verificados: number }>('/assinatura/verificar',
  { metodo: 'POST', corpo });

export const aprovarLote = (corpo: {
  ensaioIds: string[]; observacao?: string;
}) => api<{ aprovados: number }>('/assinatura/aprovar',
  { metodo: 'POST', corpo });

/**
 * O laudo do ensaio, para olhar antes de assinar.
 *
 * «Poderia abrir um pop-up do pdf de visualização rápida para cada
 *  ensaio, assim fica prático verificar 1 por 1» — Testesolo, 02/09/2026.
 *
 * É o HTML do laudo, e não o PDF: abre na hora, sem esperar a fila de
 * geração, e é o MESMO documento que o PDF imprime — o gerador é um só
 * (`D-29`). O carimbo de preliminar continua onde estava, porque o
 * ensaio ainda não foi aprovado quando se está olhando para decidir se
 * aprova.
 */
export const laudoParaConferir = (ensaioId: string) =>
  api<string>(`/ensaios/${ensaioId}/laudo`, { cru: true });
